import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Building } from 'lucide-react';
import { submitContactForm, submitProjectInquiry } from '../api/api';
import { toast } from 'sonner';

const Contact = () => {
  const [activeForm, setActiveForm] = useState('contact');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [projectForm, setProjectForm] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    location: '',
    budget: '',
    description: ''
  });

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  const handleProjectChange = (e) => {
    setProjectForm({ ...projectForm, [e.target.name]: e.target.value });
  };

  const submitContactForm_handler = async (e) => {
    e.preventDefault();
    const result = await submitContactForm(contactForm);
    if (result.success) {
      toast.success('Thank you! We will contact you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } else {
      toast.error('Failed to submit form. Please try again.');
    }
  };

  const submitProjectForm_handler = async (e) => {
    e.preventDefault();
    const result = await submitProjectInquiry(projectForm);
    if (result.success) {
      toast.success('Your project inquiry has been submitted successfully!');
      setProjectForm({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        location: '',
        budget: '',
        description: ''
      });
    } else {
      toast.error('Failed to submit inquiry. Please try again.');
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Chennai Office',
      details: '#4/570, 4th Block, Mogappair West, Chennai, Tamil Nadu 600037'
    },
    {
      icon: MapPin,
      title: 'Bengaluru Office',
      details: '#61/A, 18th main road, 3rd sector, HSR Layout, Bengaluru-102'
    },
    {
      icon: Phone,
      title: 'Phone',
      details: '+91 63795 41008'
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'info@archifab.in'
    }
  ];

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-4">
            Get in <span className="text-yellow-600">Touch</span>
          </h2>
          <div className="w-24 h-1 bg-yellow-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Ready to start your project? Contact us today for a consultation or submit a project inquiry.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-black mb-6">Let's Work Together</h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                Whether you're planning a residential, commercial, or institutional project, our team is here to bring your vision to life with creativity, expertise, and trust.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-white rounded-sm shadow-md hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">{info.title}</h4>
                      <p className="text-gray-600 text-sm">{info.details}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Map/Image Placeholder */}
            <div className="aspect-video bg-gray-200 rounded-sm overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1758193783649-13371d7fb8dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBpbnRlcmlvciUyMGRlc2lnbiUyMG1vZGVybnxlbnwwfHx8fDE3NjA5NDg3NzN8MA&ixlib=rb-4.1.0&q=85"
                alt="Office"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Forms */}
          <div className="bg-white rounded-sm shadow-xl p-8">
            {/* Form Tabs */}
            <div className="flex gap-4 mb-8 border-b border-gray-200">
              <button
                onClick={() => setActiveForm('contact')}
                className={`pb-4 px-2 font-semibold transition-colors relative ${
                  activeForm === 'contact'
                    ? 'text-yellow-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Contact Us
                {activeForm === 'contact' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600"></div>
                )}
              </button>
              <button
                onClick={() => setActiveForm('project')}
                className={`pb-4 px-2 font-semibold transition-colors relative ${
                  activeForm === 'project'
                    ? 'text-yellow-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Project Inquiry
                {activeForm === 'project' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600"></div>
                )}
              </button>
            </div>

            {/* Contact Form */}
            {activeForm === 'contact' && (
              <form onSubmit={submitContactForm_handler} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                    placeholder="+91 12345 67890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-yellow-600 text-white font-semibold rounded-sm hover:bg-yellow-700 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  Send Message
                  <Send size={18} />
                </button>
              </form>
            )}

            {/* Project Inquiry Form */}
            {activeForm === 'project' && (
              <form onSubmit={submitProjectForm_handler} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={projectForm.name}
                    onChange={handleProjectChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                    placeholder="Your full name"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={projectForm.email}
                      onChange={handleProjectChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={projectForm.phone}
                      onChange={handleProjectChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                      placeholder="+91 12345 67890"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Type *
                  </label>
                  <select
                    name="projectType"
                    value={projectForm.projectType}
                    onChange={handleProjectChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                  >
                    <option value="">Select project type</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="hospitality">Hospitality</option>
                    <option value="institutional">Institutional</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={projectForm.location}
                      onChange={handleProjectChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                      placeholder="Project location"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Budget Range
                    </label>
                    <input
                      type="text"
                      name="budget"
                      value={projectForm.budget}
                      onChange={handleProjectChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all"
                      placeholder="e.g., 50L - 1Cr"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Description *
                  </label>
                  <textarea
                    name="description"
                    value={projectForm.description}
                    onChange={handleProjectChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-all resize-none"
                    placeholder="Describe your project requirements..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-yellow-600 text-white font-semibold rounded-sm hover:bg-yellow-700 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  Submit Project Inquiry
                  <Building size={18} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;